import http from '@/utils/request';

/**
 * 账号审批状态
 *
 * 与后端 AccountStatus 枚举保持一致
 */
export enum AccountStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
}

/**
 * 账号信息
 *
 * 注：nickname 不单建列，存放在 extra.nickname；前端使用 getNickname() 派生。
 * 不再保存"微信账号"字段（snsapi_login 无法获取，也不支持人工填写）。
 */
export interface Account {
  id: number;
  openid: string;
  unionid: string | null;
  organization: string | null;
  name: string | null;
  phone: string | null;
  status: AccountStatus;
  extra: Record<string, unknown> | null;
  auditBy: string | null;
  auditAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 从 extra.nickname 安全读取昵称 */
export const getNickname = (account: Pick<Account, 'extra'> | null | undefined): string | null => {
  const v = account?.extra?.nickname;
  return typeof v === 'string' && v.trim() ? v : null;
};

/** 从 extra.headimgurl 安全读取微信头像 URL（微信原始 profile 字段） */
export const getAvatarUrl = (account: Pick<Account, 'extra'> | null | undefined): string | null => {
  const v = account?.extra?.headimgurl;
  return typeof v === 'string' && v.trim() ? v : null;
};

export interface AccountListResult {
  list: Account[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AccountListParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: AccountStatus;
}

/**
 * 更新账号入参
 *
 * 后台「账号管理」编辑弹窗统一使用本 payload：
 *  - name / organization 必填（后端 DTO 一致，前端也需强校验）
 *  - phone 选填
 *  - status 选填；不传则保持不变。用于「编辑」与「修改状态」合并后的一体化提交。
 */
export interface UpdateAccountPayload {
  id: number;
  organization: string;
  name: string;
  phone?: string;
  status?: AccountStatus;
}

/** 审批入参：拒绝无需理由 */
export interface AuditAccountPayload {
  id: number;
  status: AccountStatus.APPROVED | AccountStatus.REJECTED;
}

// 列表：纯查询、参数平坦 -> GET
export const fetchAccountList = (params: AccountListParams) =>
  http.get<unknown, AccountListResult>('/account/list', { params });

// 详情：纯查询 -> GET
export const fetchAccountDetail = (id: number) =>
  http.get<unknown, Account>('/account/detail', { params: { id } });

// 更新（补全资料）-> POST
export const updateAccount = (payload: UpdateAccountPayload) =>
  http.post<unknown, Account>('/account/update', payload);

// 审批 -> POST
export const auditAccount = (payload: AuditAccountPayload) =>
  http.post<unknown, Account>('/account/audit', payload);

// 删除（逻辑删除）-> POST，id 走 body
export const deleteAccount = (id: number) =>
  http.post<unknown, { id: number }>('/account/delete', { id });
